import React, { ReactElement } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import {
  RESOURCE_PLAN_TABS,
  TABLE_ACTION_TYPE,
  REQUEST_STATUS,
} from "@src/constants";
import {
  getAllocationChangesForRequest,
  createRequestPayload,
  mapRoleRequestData,
} from "@src/features/resource_plan_v2/utils/resource_plan_table_utils";
import {
  getDraftOptions,
  getSendOptions,
  isOptionDisabled,
  hasEmptyRequest,
} from "@src/features/resource_plan_v2/utils/table_actions_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import {
  setTotalDraftRequests,
  setCurrentTabConfig,
  setIsTableDisabled,
} from "@src/services/resourcePlanSliceV2";
import { useDeleteRequestV2Mutation } from "@src/services/slices/projectRoleRequestsApi";
import {
  useUpdateRoleRequestMutation,
  projectsApi,
} from "@src/services/slices/projectsApi";
import { useAppSelector, RootState, useAppDispatch } from "@src/setupStore";
import {
  ResourcePlanTableRowData,
  RoleRequest,
  AllocationRowData,
} from "@src/types/role_request_types";
import { IRowNode } from "ag-grid-community";
import { Select, App, Form } from "antd";
import "./table_actions.less";
import { DefaultOptionType } from "antd/es/select/index";

interface TableActionsProps {
  roleRequestData?: any[];
  disabled?: boolean;
}

const TableActions: React.FC<TableActionsProps> = ({ disabled }) => {
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const { gridApi } = useAgGrid();
  const [form] = Form.useForm();

  const {
    projectId,
    totalDraftRequests,
    jointVenturePartner,
    resourcePlanTableState: { currentTabConfig },
  } = useAppSelector((state: RootState) => state.resourcePlanSliceV2);

  const {
    draftActions: {
      isDraftActionSelectionDisabled,
      isPublishRequestOptionDisabled,
      hasSelectedDraftRequestsError,
    },
    inboxActions: {
      isInboxActionSelectionDisabled,
      isUndoRequestOptionDisabled,
      isSendRequestOptionDisabled,
      hasSelectedInboxRequestsError,
      isSelectedInboxRequestsUnchanged,
    },
  } = useAppSelector((state: RootState) => state.resourcePlanTableActionsSlice);

  // redux state
  const [updateRoleRequestMutation] = useUpdateRoleRequestMutation();
  const [deleteRoleRequest] = useDeleteRequestV2Mutation();

  const actionOptions: DefaultOptionType[] = React.useMemo(() => {
    switch (currentTabConfig?.key) {
      case RESOURCE_PLAN_TABS.DRAFTS: {
        return getDraftOptions(isPublishRequestOptionDisabled);
      }
      case RESOURCE_PLAN_TABS.RESOURCE_PLAN_INBOX:
        return getSendOptions(
          isUndoRequestOptionDisabled,
          isSendRequestOptionDisabled
        );
      default:
        return null;
    }
  }, [
    currentTabConfig,
    isPublishRequestOptionDisabled,
    isUndoRequestOptionDisabled,
    isSendRequestOptionDisabled,
  ]);

  const hasError: boolean = React.useMemo(() => {
    switch (currentTabConfig?.key) {
      case RESOURCE_PLAN_TABS.DRAFTS: {
        return hasSelectedDraftRequestsError;
      }
      case RESOURCE_PLAN_TABS.RESOURCE_PLAN_INBOX:
        return hasSelectedInboxRequestsError;
      default:
        return false;
    }
  }, [
    currentTabConfig,
    hasSelectedDraftRequestsError,
    hasSelectedInboxRequestsError,
  ]);

  const hasChanges: boolean = React.useMemo(() => {
    switch (currentTabConfig?.key) {
      case RESOURCE_PLAN_TABS.DRAFTS: {
        return isPublishRequestOptionDisabled;
      }
      case RESOURCE_PLAN_TABS.RESOURCE_PLAN_INBOX:
        return isSelectedInboxRequestsUnchanged;
      default:
        return false;
    }
  }, [
    currentTabConfig,
    isSelectedInboxRequestsUnchanged,
    isPublishRequestOptionDisabled,
  ]);

  const isTableActionSelectionDisabled = React.useMemo(() => {
    return currentTabConfig?.key === RESOURCE_PLAN_TABS.DRAFTS
      ? isDraftActionSelectionDisabled
      : isInboxActionSelectionDisabled;
  }, [
    currentTabConfig,
    isDraftActionSelectionDisabled,
    isInboxActionSelectionDisabled,
  ]);

  /**
   * Get the currently selected requests based on the current tab
   * @returns the selected requests of the current tab
   */
  function getSelectedRequestsOfCurrentTab(): ResourcePlanTableRowData[] {
    return currentTabConfig?.key === RESOURCE_PLAN_TABS.DRAFTS
      ? gridApi
          ?.getSelectedRows()
          .filter(
            (resourcePlanTableRowData: ResourcePlanTableRowData) =>
              resourcePlanTableRowData.status === REQUEST_STATUS.DRAFT
          )
      : gridApi
          ?.getSelectedRows()
          .filter(
            (resourcePlanTableRowData: ResourcePlanTableRowData) =>
              resourcePlanTableRowData.status !== REQUEST_STATUS.DRAFT
          );
  }

  /**
   * Get the requests to send based on the current tab.
   *
   * @returns The requests to send
   */
  function getAllSelectedAndUpdatedRequests(): ResourcePlanTableRowData[] {
    return currentTabConfig.key === RESOURCE_PLAN_TABS.DRAFTS
      ? getUpdatedDraftRequests()
      : getUpdatedInboxRequests();
  }

  /**
   * Get all draft requests with allocations. This should prevent from sending empty draft requests when the
   * user selected multiple requests and one of them was emptier than the rest will be send only the empty one not
   */
  function getUpdatedDraftRequests(): ResourcePlanTableRowData[] {
    return getSelectedRequestsOfCurrentTab().filter(
      (request: ResourcePlanTableRowData) => request.allocations.size !== 0
    );
  }

  /**
   * Get the updated requests from the inbox tab
   * @param withEmptyRequests will be true if it should return requests with no allocations at all.
   * @returns the updated requests
   * */
  function getUpdatedInboxRequests(
    withEmptyRequests?: boolean
  ): ResourcePlanTableRowData[] {
    return getSelectedRequestsOfCurrentTab().filter(
      (request: ResourcePlanTableRowData) => {
        const allocations: AllocationRowData[] = Array.from(
          request.allocations.values()
        );

        const hasUpdatedRequest: boolean = allocations.some(
          (allocation: AllocationRowData) => {
            return allocation.updatedPercentage != null;
          }
        );

        return withEmptyRequests
          ? hasUpdatedRequest
          : hasUpdatedRequest && !hasEmptyRequest(allocations);
      }
    );
  }

  /**
   * Undo changes on selected request(s)
   */
  function undoChanges(): void {
    const updatedRequests: ResourcePlanTableRowData[] =
      getUpdatedInboxRequests(true);

    for (const updatedRequest of updatedRequests) {
      for (const value of updatedRequest.allocations.values()) {
        delete value.updatedPercentage;
      }

      updateTableState([updatedRequest]);
    }

    resetTableState();
  }

  /**
   * Send selected request(s)
   */
  async function sendRequests(): Promise<void> {
    const currentTabSelectedRequests: ResourcePlanTableRowData[] =
      getAllSelectedAndUpdatedRequests();

    const updatedRequests: RoleRequest[] = [];

    for (const selectedRequest of currentTabSelectedRequests) {
      const sendRoleRequest: RoleRequest = await sendSingleRequest(
        selectedRequest
      );

      if (sendRoleRequest) {
        // if there is no request in the inbox and the current request is the only one draft request than it happens that the table do not update and
        // shows the request as draft. So we need to set the current tab config to drafts
        if (currentTabConfig?.key === RESOURCE_PLAN_TABS.DRAFTS) {
          dispatch(
            setCurrentTabConfig({
              icon: undefined,
              key: "drafts",
              label: "drafts",
            })
          );
        }

        updateTableState(
          mapRoleRequestData([sendRoleRequest], jointVenturePartner)
        );
        gridApi.setGridOption("loadingOverlayComponentParams", {
          text: `Sent request ${updatedRequests.length} of ${currentTabSelectedRequests.length}.`,
        });
        updatedRequests.push(sendRoleRequest);
      }
    }

    resetTableState();

    if (updatedRequests.length !== 0) {
      updateCachedRoleRequest(updatedRequests);
      await onSuccess(
        updatedRequests.length,
        `Successfully sent ${updatedRequests.length} request(s)`
      );
    }
  }

  /**
   * Send a single request
   *
   * @param request The request to send
   *
   * @returns The sent request or null if failed
   */
  async function sendSingleRequest(
    request: ResourcePlanTableRowData
  ): Promise<RoleRequest | null> {
    try {
      return await updateRoleRequestMutation({
        projectId,
        roleRequestId: request.roleRequestId.toString(),
        requestPayload: createRequestPayload(
          request,
          getAllocationChangesForRequest(request),
          false
        ),
      }).unwrap();
    } catch (error) {
      message.error("Failed to send selected Request");
    }
  }

  /**
   * Delete selected request(s)
   */
  async function deleteRequests(): Promise<void> {
    const deletedRequestIds: string[] = [];
    const selectedRequests: ResourcePlanTableRowData[] =
      getSelectedRequestsOfCurrentTab();

    for (const [index, selectedRequest] of selectedRequests.entries()) {
      try {
        const roleRequestId = selectedRequest.roleRequestId.toString();
        const deletedRequestId: string = await deleteRoleRequest(
          roleRequestId
        ).unwrap();

        if (deletedRequestId) {
          deletedRequestIds.push(deletedRequestId);

          const rowNode: IRowNode = gridApi.getRowNode(roleRequestId);
          gridApi.applyTransaction({ remove: [rowNode.data] });
          gridApi.setGridOption("loadingOverlayComponentParams", {
            text: `Deleted request ${index + 1} of ${selectedRequests.length}.`,
          });
        }
      } catch (error) {
        console.log("Error deleting request:", error);
      }
    }

    resetTableState();

    if (deletedRequestIds.length === selectedRequests.length) {
      deleteCachedRoleRequest(deletedRequestIds);
      await onSuccess(
        deletedRequestIds.length,
        `Successfully deleted ${deletedRequestIds.length} request(s)`
      );
    }
  }

  /**
   * Delete the cached role request from the redux store
   *
   * @param roleRequestIds ids of the role requests to delete
   */
  function deleteCachedRoleRequest(roleRequestIds: string[]): void {
    dispatch(
      projectsApi.util.updateQueryData(
        "getRoleRequestsByProjectId",
        projectId,
        (roleRequests = []) => {
          for (const deletedRequestId of roleRequestIds) {
            const index = roleRequests.findIndex(
              (request) => request.roleRequestId === parseInt(deletedRequestId)
            );
            if (index !== -1) {
              console.log("deletedRequestId", deletedRequestId);
              roleRequests.splice(index, 1);
            }
          }
        }
      ) as any
    );
  }

  /**
   * Update the cached role request in the redux store
   *
   * @param updatedRequests The updated role requests
   */
  function updateCachedRoleRequest(updatedRequests: RoleRequest[]) {
    dispatch(
      projectsApi.util.updateQueryData(
        "getRoleRequestsByProjectId",
        projectId,
        (roleRequests = []) => {
          for (const updatedRequest of updatedRequests) {
            const index = roleRequests.findIndex(
              (request) =>
                request.roleRequestId === updatedRequest.roleRequestId
            );
            if (index !== -1) {
              roleRequests[index] = updatedRequest;
            }
          }
        }
      ) as any
    );
  }

  /**
   * Update the table state with the new data
   *
   * @param roleRequests The role request data to update
   */
  function updateTableState(roleRequests: ResourcePlanTableRowData[]): void {
    gridApi.applyTransaction({
      update: roleRequests,
    });

    for (const request of roleRequests) {
      const rowNode: IRowNode = gridApi.getRowNode(
        request.roleRequestId.toString()
      );
      if (rowNode) {
        rowNode.setSelected(false);
      }
    }
  }

  /**
   * Reset the table state
   */
  function resetTableState(): void {
    dispatch(setIsTableDisabled(false));
    gridApi.setGridOption("loading", false);
    gridApi.clearCellSelection();
    gridApi.clearFocusedCell();
  }

  /**
   * Show success message and update the state
   *
   * @param totalRequests The total number of requests
   * @param  messageText The message text to show
   */
  async function onSuccess(
    totalRequests: number,
    messageText: string
  ): Promise<void> {
    if (totalRequests === 0) return;

    await message.success(messageText);

    if (currentTabConfig?.key === RESOURCE_PLAN_TABS.DRAFTS) {
      dispatch(setTotalDraftRequests(totalDraftRequests - totalRequests));
    }
  }

  /**
   * Handle select action from the dropdown
   * @param selectedTableAction selected value
   */
  async function handleSelect(
    selectedTableAction: TABLE_ACTION_TYPE
  ): Promise<void> {
    form.resetFields();
    gridApi.setGridOption("loadingOverlayComponentParams", {
      text: "Loading ...",
    });
    gridApi.setGridOption("loading", true);
    dispatch(setIsTableDisabled(true));

    switch (selectedTableAction) {
      case TABLE_ACTION_TYPE.UNDO:
        undoChanges();
        break;
      case TABLE_ACTION_TYPE.SEND:
        await sendRequests();
        break;
      case TABLE_ACTION_TYPE.DELETE:
        await deleteRequests();
        break;
    }
  }

  /**
   * Render the select options
   *
   * @param option The option to render
   *
   * @returns The rendered option
   */
  function optionRender(option: DefaultOptionType): React.ReactNode {
    const isDisabled: boolean = isOptionDisabled(
      currentTabConfig?.key,
      option,
      isPublishRequestOptionDisabled,
      isSendRequestOptionDisabled,
      isUndoRequestOptionDisabled
    );

    return (
      <div
        className={`table-actions__option ${
          isDisabled ? "table-actions__option--disabled" : ""
        }`}
      >
        {option.data.icon}
        {option.data.desc}
      </div>
    );
  }

  /**
   * Will render a notification info when the selected request has errors or no changes.
   *
   * @param menu The react option element
   * @param hasError true or false if errors exists or not and the option is enabled or not.
   * @param hasChanges true or false if there are changes and the option is enabled or not.
   */
  function notificationSelectionRenderer(
    menu: ReactElement,
    hasError: boolean,
    hasChanges: boolean
  ) {
    const ErrorMessage: React.FC<{ children: React.ReactNode }> = ({
      children,
    }) => {
      const className = hasError ? "error" : "info";

      return (
        <div
          className={`table-actions__option table-actions__notification-section table-actions__notification-section--${className}`}
          role="notification"
        >
          <InfoCircleOutlined /> {children}
        </div>
      );
    };

    return (
      <>
        {hasError && (
          <ErrorMessage>
            Some selected requests have errors or are invalid.
          </ErrorMessage>
        )}
        {!hasError && hasChanges && (
          <ErrorMessage>The selected requests have not changed.</ErrorMessage>
        )}
        {menu}
      </>
    );
  }

  return (
    <div className="table-actions">
      <Form form={form}>
        <Form.Item name="action-option">
          <Select
            variant="filled"
            placeholder="Actions"
            onSelect={handleSelect}
            popupMatchSelectWidth={false}
            size="large"
            disabled={isTableActionSelectionDisabled || disabled}
            optionRender={optionRender}
            options={actionOptions}
            popupRender={(menu: ReactElement) =>
              notificationSelectionRenderer(menu, hasError, hasChanges)
            }
            classNames={{ popup: { root: "table-actions__dropdown" } }}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default TableActions;
